import { DestroyRef, inject, Injectable } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnalysisResults, AppAccessLevel } from '@sleipai/sleip-models';
import { combineLatest, from, Observable } from 'rxjs';
import {
  collection,
  collectionSnapshots,
  doc,
  docSnapshots,
  documentId,
  Firestore,
  query,
  where,
} from '@angular/fire/firestore';
import {
  Functions,
  getFunctions,
  httpsCallable,
} from '@angular/fire/functions';
import { getApp } from '@angular/fire/app';

@Injectable()
export class AnalysisResultsService {
  constructor() {}

  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private destroyRef = inject(DestroyRef);

  loadSingleAnalysisById(id: string, isFlexion: boolean): Observable<AnalysisResults> {
    const analysisResultRef = doc(this.firestore, `analysisResults/${id}`);
    const analysisResultDocData$ = docSnapshots(analysisResultRef);

    if (isFlexion) {
      const flexionResultRef = doc(
        this.firestore,
        `analysisResults/${id}/variantAnalysisResults/flexion`
      );
      const flexionResultDocData$ = docSnapshots(flexionResultRef);

      return combineLatest([
        analysisResultDocData$,
        flexionResultDocData$,
      ]).pipe(
        take(1),
        map(([analysisResultSnap, flexionResultSnap]) => {
          const originalData = analysisResultSnap.data() as AnalysisResults;
          const flexionData = flexionResultSnap.data() as AnalysisResults;

          return {
            ...originalData,
            ...flexionData,
            id: analysisResultSnap.id,
          };
        })
      );
    } else {
      return analysisResultDocData$.pipe(
        take(1),
        map((analysisResultSnap) => {
          return {
            id: analysisResultSnap.id,
            ...(analysisResultSnap.data() as AnalysisResults),
          };
        })
      );
    }
  }

  loadMultipleAnalysisResultsByIds(ids: string[]) {
    const analysisResultsRef = collection(this.firestore, 'analysisResults');
    let analysisResultQuery = query(analysisResultsRef);
    if (ids && ids.length > 0) {
      analysisResultQuery = query(
        analysisResultQuery,
        where(documentId(), 'in', ids)
      );
    }

    return collectionSnapshots(analysisResultQuery).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((analysisList) => {
        return analysisList.map((result) => {
          const data = result.data();
          const id = result.id;

          return {
            ...data,
            id,
          } as AnalysisResults;
        });
      })
    );
  }

  generateShardAnalysisResultLink(
    horseId: string,
    recordingIds: string[],
    version: string = 'v2'
  ) {
    const webSharingPayload = {
      recordingIds,
      horseId: horseId,
      version,
    };
    const generateSharedAnalysisResultLinkCallable = httpsCallable(
      this.functions,
      'publicSharedRecordingsApi-put'
    );
    return from(
      generateSharedAnalysisResultLinkCallable(webSharingPayload)
    ).pipe(map((data) => data.data as string));
  }

  generateAnalysisResultPDF(
    horseId: string,
    recordingIds: string[],
    userType: AppAccessLevel,
    timeZone: string
  ) {
    const europeWest4RegionFunctions = getFunctions(getApp(), 'europe-west4');

    const pdfGeneratingPayload = {
      recordingIds,
      horseId,
      userType,
      timeZone,
    };
    const generateAnalysisResultPDFCallable = httpsCallable(
      europeWest4RegionFunctions,
      'generatePdf-api'
    );
    return from(generateAnalysisResultPDFCallable(pdfGeneratingPayload)).pipe(
      map((data) => data.data)
    );
  }
}
